// Isometric projection math. Deliberately Pixi-free so gameplay systems (the
// cursor harvest hit-test in particular) can share the exact same tile->screen
// mapping the renderer uses. A single mutable `camera` is the one source of
// truth for where tile (0,0) lands on screen; the renderer updates it on
// resize, everything else just reads it.

export const TILE_W = 64; // full diamond width in px
export const TILE_H = 32; // full diamond height in px

export const camera = {
  x: 0, // screen x where tile (0,0) centers
  y: 0, // screen y where tile (0,0) centers
  zoom: 1,
};

export interface ScreenPoint {
  x: number;
  y: number;
}

/** Tile grid coordinate -> UNZOOMED local pixel, relative to tile (0,0). Sprites
 *  live inside a single world container that applies the camera (offset + zoom),
 *  so they are placed with these local coordinates. */
export function worldToLocal(gx: number, gy: number): ScreenPoint {
  return {
    x: (gx - gy) * (TILE_W / 2),
    y: (gx + gy) * (TILE_H / 2),
  };
}

/** Tile grid coordinate -> screen pixel (canvas-local), applying the camera.
 *  Used by the cursor harvest hit-test, which works in screen space. */
export function worldToScreen(gx: number, gy: number): ScreenPoint {
  const l = worldToLocal(gx, gy);
  return {
    x: camera.x + l.x * camera.zoom,
    y: camera.y + l.y * camera.zoom,
  };
}

/** Screen pixel -> nearest tile coordinate (inverse projection). */
export function screenToWorld(sx: number, sy: number): { gx: number; gy: number } {
  const z = camera.zoom;
  const dx = (sx - camera.x) / z;
  const dy = (sy - camera.y) / z;
  const a = dx / (TILE_W / 2);
  const b = dy / (TILE_H / 2);
  return { gx: (a + b) / 2, gy: (b - a) / 2 };
}

/** Depth key for painter's-algorithm sorting (back to front). */
export function depth(gx: number, gy: number): number {
  return gx + gy;
}

export function setCamera(x: number, y: number, zoom = camera.zoom): void {
  camera.x = x;
  camera.y = y;
  camera.zoom = zoom;
}
